import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSaveReport from './useSaveReport';
import useDeleteReport from './useDeleteReport';
import useCopyReport from './useCopyReport';
import { stores } from '../../../stores';
import { Reports } from '../../../api/relevant';
import useReports from '../../../hooks/useBreakpoint/useReports';
import classes from '../../../api/classes';
import Base from '../../../layouts/Base';
import PopupSelector from '../../PopupSelector';
import { ReportTemplateSettings } from '../../ReportTemplatesManage/ReportTemplateSettings';
import { maybeCopyToDashboard } from '../../ReportTemplatesManage/utils';
import BrowserUtils from '../../../lib/browserUtils';

const { ReportTemplate } = classes;

function useReportActions({
	isPrivate,
	isDirty,
	report,
	type,
	pinnedByUsers,
	reportComponentRef,
	setStatus,
}) {
	const currentUser = stores.identity.user();
	// Convert backend subscription objects to email-occurrence pairs (with optional user information)
	const cleanedSubscriptions = report?.subscriptions?.[0]?.occurences ? report.subscriptions.flatMap(({ id, emailOnlyRecipients, userRecipients, occurences, addedByUserId }) => {
		const recipients = [
			...emailOnlyRecipients.map((email) => ({ email })),
			...userRecipients,
		];
		const newSubs = recipients.flatMap((recipient) => occurences.map(({ type: occurrence }) => ({
			recipient,
			occurrence,
			addedByUserId,
		})));
		// Make ids unique (this is used only in the frontend for keys)
		return newSubs.map((newSub, i) => ({ ...newSub, id: `${id}-${i}` }));
	}) : report?.subscriptions;
	const [subscriptions, setSubscriptions] = useState(cleanedSubscriptions || []);
	const [pinnedToSidebar, setPinnedToSidebar] = useState(pinnedByUsers.includes(currentUser.id));
	const { reports, updateReports } = useReports();

	const onSave = useSaveReport({
		isPrivate,
		report,
		reportComponentRef,
		setStatus,
		type,
		subscriptions,
	});

	const history = useHistory();
	const template = useRef(null);
	const onSaveTemplate = useCallback(async () => {
		const settings = reportComponentRef.current.getCurrentProperties();
		template.current = new ReportTemplate({
			settings,
		});

		Base.renderGlobal((close) => (
			<PopupSelector
				forceExpanded
				title="Template Settings"
				onApplyChanges={async () => {
					await ReportTemplate.save(template.current);
					ReportTemplate.fnCache.reset();
					history.push('/settings/report-templates');
					close();
				}}
				onCancel={close}
				content={() => (
					<ReportTemplateSettings
						template={template.current}
						onChange={(values) => {
							Object.assign(template.current, values);
						}}
					/>
				)}
			/>
		));
	}, [reportComponentRef, history]);

	const onCopyToDashboard = useCallback(async () => {
		const settings = reportComponentRef.current.getCurrentProperties();
		await maybeCopyToDashboard({ settings });
	}, [reportComponentRef]);

	const onCopy = useCopyReport({ report, type, reportComponentRef, isDirty });

	const onDelete = useDeleteReport({
		reportId: report?.id,
		report,
	});

	const onExport = useCallback(async () => {
		return reportComponentRef.current.exportAsCsv();
	}, [reportComponentRef]);

	const onPinClick = useCallback(async () => {
		const updatedPinned = !pinnedToSidebar;
		try {
			await Reports.call('updatePinnedToSidebar', {
				reportIds: [report?.id],
				pinned: updatedPinned,
			});
			setPinnedToSidebar(updatedPinned);
			updateReports();
		} catch (err) {
			// Revert if it fails
			setPinnedToSidebar(!updatedPinned);
		}
	}, [pinnedToSidebar, report?.id, updateReports]);

	return {
		onSave,
		onDelete,
		onExport,
		onSaveTemplate,
		onPinClick,
		onCopyToDashboard,
		onCopy,
		subscriptions,
		setSubscriptions,
		pinnedToSidebar,
	};
}

export default useReportActions;
